import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useScene } from 'src/hooks/useScene';
import { getTilePosition } from 'src/utils';
import { Label } from 'src/components/Label/Label';
import { useColor } from '@/hooks/useColor';
import { getRectangleLabelLayout } from '@/utils/rectangleLabelLayout';

interface Props {
  rectangle: ReturnType<typeof useScene>['rectangles'][0];
}

export const RectangleLabel = ({ rectangle }: Props) => {
  const color = useColor(rectangle.color);
  const labelColor = color?.value || '#3b82f6';

  const labelLayout = useMemo(() => {
    return getRectangleLabelLayout(
      rectangle.from,
      rectangle.to,
      rectangle.labelPosition || 'CENTER'
    );
  }, [rectangle.from, rectangle.to, rectangle.labelPosition]);

  const labelPosition = useMemo(() => {
    return getTilePosition({ tile: labelLayout.tile });
  }, [labelLayout.tile]);

  if (!rectangle.description || rectangle.description.trim() === '') {
    return null;
  }

  const maxWidth = 300;
  return (
    <Box
      sx={{
        position: 'absolute',
        pointerEvents: 'none',
        transform: `rotate(${labelLayout.rotation}deg)`,
        transformOrigin: '0 0'
      }}
      style={{
        left: labelPosition.x + labelLayout.offset.x,
        top: labelPosition.y + labelLayout.offset.y
      }}
    >
      <Label
        maxWidth={maxWidth}
        labelHeight={0}
        sx={{
          zIndex: 10,
          px: 2,
          py: 0.75,
          bgcolor: `${labelColor}`,
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: 600,
          borderRadius: '6px',
          boxShadow: `0 4px 12px ${labelColor}60`,
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          background: `linear-gradient(135deg, ${labelColor} 0%, ${labelColor}e8 100%)`,
          letterSpacing: '0.025em'
        }}
      >
        {rectangle.description}
      </Label>
    </Box>
  );
};
